import React, { useEffect } from 'react';

import Hotjar from '@hotjar/browser';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import usePlacesAutocomplete from 'use-places-autocomplete';

import TranscriptAuthorization from 'pages/PreQualification/steps/ContractorInfo/TranscriptAuthorization/TranscriptAuthorization';
import { PreQualificationStep, setPreQualificationStep, setSignInStep, SignInStep } from 'actions';
import PrivateRoute from 'routes/PrivateRoute';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import useBackListener from 'hooks/useBackListener';
import useScript from 'hooks/useScript';
import useAutoScroll from 'hooks/useScrollUp';
import { GooglePlacesContext } from 'index';
import ContractorPortal from 'pages/ContractorPortal/ContractorPortal';
import Dashboard from 'pages/Dashboard';
import Error from 'pages/Error/Error';
import Main from 'pages/Main';
import NotFound from 'pages/NotFound';
import PreQualification from 'pages/PreQualification';
import AcceptTermsOfUse from 'pages/PreQualification/steps/AcceptTermsOfUse';
import AcknowledgementAndConsent from 'pages/PreQualification/steps/AcknowledgementAndConsent';
import CompleteMobileAuth from 'pages/PreQualification/steps/CompleteMobileAuth';
import VerificationComplete from 'pages/PreQualification/steps/VerificationComplete';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import SignInProcess from 'pages/SignInProcess';
import EnterNewPassword from 'pages/SignInProcess/steps/EnterNewPassword';
import TermsOfUse from 'pages/TermsOfUse';
import NotQualified from 'pages/NotQualified/NotQualified';
import { PasswordProtection } from 'pages/PasswordProtection/PasswordProtection';
import { PasswordProtectedRoute } from 'pages/PasswordProtection/PasswordProtectedRoute';
import MobileSession from 'pages/Dashboard/Identity/ValidationId/MobileSession';
import { ApplicationEnvironments } from 'enums/common.enums';
import MasterDealerAgreementDocument from 'pages/ContractorPortal/steps/MasterDealerAgreement';
import ESignConsentDisclosure from 'pages/PreQualification/steps/ESignConsentDisclosure';
import { ENVIRONMENT, IS_HOTJAR_ENABLED } from 'constants/constants';

import { AppRoutes } from './routes.enums';

export default () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const { currentPreQualificationStep, currentSignInStep, currentDashboardStep } = useAppSelector(
    (state) => state.stages,
  );
  const loanType = useAppSelector((state) => state.application[LoanInformationVariables.LoanType]);
  const { pathname, search: query } = useLocation();

  useEffect(() => {
    if (!history?.state?.usr?.hfsPortal) {
      navigator(`${pathname}${query}`, { state: { hfsPortal: true } });
    }
  }, []);

  useEffect(() => {
    if (!IS_HOTJAR_ENABLED) {
      return;
    }

    let hotjarAction = '';

    switch (pathname) {
      case AppRoutes.Prequalification:
        hotjarAction = pathname + currentPreQualificationStep;
        break;
      case AppRoutes.Login:
        hotjarAction = pathname + currentPreQualificationStep;
        break;
      case AppRoutes.Dashboard:
        hotjarAction = pathname + currentPreQualificationStep;
        break;
      default:
        hotjarAction = pathname;
    }

    Hotjar.event(hotjarAction);
  }, [pathname, currentPreQualificationStep, currentSignInStep, currentDashboardStep]);

  useBackListener(() => {
    setTimeout(() => {
      if ([AppRoutes.AcceptTermsOfUse, AppRoutes.AcknowledgementAndConsent].includes(pathname as AppRoutes)) {
        dispatch(setPreQualificationStep(PreQualificationStep.Contractor));
      }

      if (pathname === AppRoutes.Prequalification) {
        switch (currentPreQualificationStep) {
          case PreQualificationStep.BorrowerInfo:
            dispatch(setPreQualificationStep(PreQualificationStep.LoanAmount));
            break;
          case PreQualificationStep.BorrowerIncome:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
            break;
          case PreQualificationStep.CoBorrowerInfo:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
            break;
          case PreQualificationStep.CoBorrowerIncome:
            dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
            break;
          case PreQualificationStep.BorrowerPhoneVerification:
            dispatch(setPreQualificationStep(PreQualificationStep.LoanAmount));
            break;
          case PreQualificationStep.CoBorrowerPhoneVerification:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
            break;
          case PreQualificationStep.Contractor:
            dispatch(
              setPreQualificationStep(
                loanType === ApplicationType.Joint
                  ? PreQualificationStep.CoBorrowerIncome
                  : PreQualificationStep.BorrowerIncome,
              ),
            );
            break;
        }
      }

      if (pathname === AppRoutes.Login || pathname === AppRoutes.IdentityVerification) {
        switch (currentSignInStep) {
          case SignInStep.ForgotPassword:
            dispatch(setSignInStep(SignInStep.SignIn));
            break;
          case SignInStep.SentResetPasswordLink:
            dispatch(setSignInStep(SignInStep.ForgotPassword));
            break;
          case SignInStep.SignIn:
            navigator(AppRoutes.Prequalification);

            return;
        }
      }

      if (pathname === AppRoutes.Dashboard) {
        return navigator(AppRoutes.Dashboard);
      }
    }, 0);
  });

  const placesHookReturn = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
    },
    debounce: 300,
    callbackName: 'initAutocomplete',
  });

  useScript(
    `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places&callback=initAutocomplete&loading=async`,
  );

  if (ENVIRONMENT === ApplicationEnvironments.Production) {
    useScript(
      `https://tag.pearldiver.io/ldc.js?pid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_PID}&aid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_AID}`,
    );
  }

  useAutoScroll();

  return (
    <GooglePlacesContext.Provider value={placesHookReturn}>
      <Routes>
        <Route path={''} element={<PasswordProtectedRoute />}>
          <Route element={<Main />}>
            <Route path={AppRoutes.Home} element={<Navigate replace to={`${AppRoutes.Prequalification}${query}`} />} />
            <Route path={AppRoutes.Prequalification} element={<PreQualification />} />
            <Route path={AppRoutes.AcceptTermsOfUse} element={<AcceptTermsOfUse />} />
            <Route path={AppRoutes.Disclosure} element={<ESignConsentDisclosure />} />
            <Route path={AppRoutes.TranscriptAuthorization} element={<TranscriptAuthorization />} />
            <Route path={AppRoutes.AcknowledgementAndConsent} element={<AcknowledgementAndConsent />} />
            <Route path={AppRoutes.Dashboard} element={<PrivateRoute />}>
              <Route path={AppRoutes.Dashboard} element={<Dashboard />} />
            </Route>
            <Route path={AppRoutes.LoanPortal} element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />} />
            <Route
              path={AppRoutes.OfferProcessing}
              element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />}
            />
            <Route path={AppRoutes.Login} element={<SignInProcess />} />
            <Route path={AppRoutes.IdentityVerification} element={<SignInProcess differentEmail />} />
            <Route path={AppRoutes.ResetPassword} element={<EnterNewPassword />} />
            <Route path={AppRoutes.CompleteAuthentication} element={<VerificationComplete />} />
            <Route path={AppRoutes.CompleteMobileAuthentication} element={<CompleteMobileAuth />} />
            <Route path={AppRoutes.ContractorPortal} element={<ContractorPortal />} />
            <Route path={AppRoutes.MasterDealerAgreement} element={<MasterDealerAgreementDocument />} />
            <Route path={AppRoutes.Other} element={<NotFound />} />
            <Route path={AppRoutes.Error} element={<Error />} />
            <Route path={AppRoutes.NotQualified} element={<NotQualified />} />
            <Route path={AppRoutes.TermOfUse} element={<TermsOfUse />} />
            <Route path={AppRoutes.PrivacyPolicy} element={<PrivacyPolicy />} />
            <Route path={AppRoutes.ValidationSession} element={<MobileSession />} />
          </Route>
        </Route>
        <Route path={AppRoutes.PasswordProtection} element={<PasswordProtection />} />
      </Routes>
    </GooglePlacesContext.Provider>
  );
};
